import type { CalculatedKPIs, TenKCategory, TimeSavedUnit } from "../types/use-case.types"

const DAYS = 220
const HOURS_PER_DAY = 8
const WEEKS = 48
const MONTHS = 12
const ANN_HOURS = DAYS * HOURS_PER_DAY // 1760

export function calculateKPIs(params: {
  timeSavedHours: number
  timeSavedUnit: TimeSavedUnit
  users: number
  salary: number
  directYear: number
  investment: number
  opCost: number
  opCostUnit: "monthly" | "yearly"
}): CalculatedKPIs {
  const {
    timeSavedHours,
    timeSavedUnit,
    users,
    salary,
    directYear,
    investment,
    opCost,
    opCostUnit,
  } = params

  // Annual hours saved per person
  let annualHoursPerPerson: number
  if (timeSavedUnit === "week") annualHoursPerPerson = timeSavedHours * WEEKS
  else if (timeSavedUnit === "month") annualHoursPerPerson = timeSavedHours * MONTHS
  else annualHoursPerPerson = timeSavedHours

  // Hourly rate
  const hourlyRate = salary / ANN_HOURS

  // Total capacity hours across all users
  const totalCapacityHours = annualHoursPerPerson * users

  // FTE equivalent
  const fteEquivalent = totalCapacityHours / ANN_HOURS

  // Soft savings (time value freed)
  const softSavings = totalCapacityHours * hourlyRate

  // Hard savings (direct savings / revenue increase)
  const hardSavings = directYear

  // Annual benefit
  const annualBenefit = softSavings + hardSavings

  // Annual operating cost
  const opCostYearly = opCostUnit === "monthly" ? opCost * MONTHS : opCost

  // ROI after 12 months
  const roi12m = annualBenefit - opCostYearly - investment

  // $10K Rule
  const tenkQuarterly = annualBenefit / 4
  const tenkMonthly = annualBenefit / 12
  const tenkWeekly = annualBenefit / 52

  let tenkCategory: TenKCategory
  if (tenkWeekly >= 10000) tenkCategory = "Low success rate"
  else if (tenkMonthly >= 10000) tenkCategory = "Ambitious"
  else if (tenkQuarterly >= 10000) tenkCategory = "Realistic"
  else tenkCategory = "Minimal"

  return {
    annualHoursPerPerson,
    hourlyRate,
    totalCapacityHours,
    fteEquivalent,
    softSavings,
    hardSavings,
    annualBenefit,
    opCostYearly,
    roi12m,
    tenkQuarterly,
    tenkCategory,
  }
}

export function calculateFromUseCaseData(data: {
  timeSavedHours: string
  timeSavedUnit: string
  users: string
  salary: string
  directYear: string
  investment: string
  opCost: string
  opCostUnit: string
}): CalculatedKPIs {
  return calculateKPIs({
    timeSavedHours: parseFloat(data.timeSavedHours) || 0,
    timeSavedUnit: data.timeSavedUnit as "week" | "month" | "once",
    users: parseInt(data.users) || 0,
    salary: parseFloat(data.salary) || 70000,
    directYear: parseFloat(data.directYear) || 0,
    investment: parseFloat(data.investment) || 0,
    opCost: parseFloat(data.opCost) || 0,
    opCostUnit: data.opCostUnit as "monthly" | "yearly",
  })
}

export { ANN_HOURS, DAYS, HOURS_PER_DAY, MONTHS, WEEKS }
